import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, Button, Table, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { CCol, CFormLabel, CFormFeedback, CFormInput, CButton } from '@coreui/react';
import { useDispatch } from 'react-redux';
import PageWrapper from '../../pagecomponents/Common/PageWrapper';
import { getCustomerData } from '../../redux/slices/CustomerSlice';
import { getJobDocList } from '../../redux/slices/JobSlice';
import { addInvoice } from '../../redux/slices/InvoiceSlice';
import { checkEmpty, toastMessage, checkFloat, formatTotal } from '../../helpers/utility.js';
import { ToastContainer } from 'react-toastify';
import useDebouncedValue from '../../helpers/useDebouncedValue';

const INIT_CUSTOMER_PARAM = {
  customer_Id: 0,
  customer_Name: '',
};

const INIT_FORM_DATA = {
  customer_Id: 0,
  customer_Name: '',
  // invoice_No: "",
  invoice_Date: format(new Date(), 'yyyy-MM-dd'),
  invoice_Remarks: '',
  custom_declaration: '',
  invoice_AWBL_Number: '',
  invoice_Mode: '',
  job_list: [],
};

const InvoiceCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [customerParam, setCustomerParam] = useState(INIT_CUSTOMER_PARAM);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteData, setAutocompleteData] = useState([]);
  const [docList, setDocList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [processInvoice, setProcessInvoice] = useState(false);
  const [formData, setFormData] = useState(INIT_FORM_DATA);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const debouncedCustomerSearch = useDebouncedValue(customerSearch, 350);
  const latestQueryRef = useRef('');

  const blurTimerRef = useRef(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null); // store selected customer object

  useEffect(() => {
    document.querySelector("input[name='customer_Name']").setAttribute('autocomplete', 'off');
  }, []);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  const getCustomerList = (e) => {
    const { value } = e.target;

    // if user is editing text, only clear selection if text no longer matches selected customer name
    setCustomerParam((prev) => {
      const next = { ...prev, customer_Name: value };

      if (selectedCustomer && value === selectedCustomer.customer_Name) {
        // keep selected
        next.customer_Id = selectedCustomer.customer_Id;
      } else {
        // user is typing something else -> selection should be cleared
        next.customer_Id = 0;
        setSelectedCustomer(null);

        // also reset invoice process stuff (only when selection is cleared)
        setProcessInvoice(false);
        setDocList([]);
      }

      return next;
    });

    setCustomerSearch(value);
    if (!value.trim()) {
      setAutocompleteData([]);
      setShowAutocomplete(false);
      return;
    }
  };

  useEffect(() => {
    const q = String(debouncedCustomerSearch || '').trim();
    if (!q) {
      setAutocompleteData([]);
      setShowAutocomplete(false);
      return;
    }

    let isMounted = true;
    latestQueryRef.current = q;

    const fetchSuggestions = async () => {
      try {
        const param = {
          sorting: 'asc',
          sorting_key: 'created_at',
          skip: 0,
          take: 100,
          searchStr: q,
          filter: {
            customer_Id: 0,
            customer_Registration_No: '',
            customer_Code: '',
          },
          useLookupApi: true,
        };

        const { payload } = await dispatch(getCustomerData({ inputData: param }));
        if (!isMounted || latestQueryRef.current !== q) return;

        const rows = Array.isArray(payload?.data?.data)
          ? payload.data.data
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

        const unique = Array.from(new Map(rows.map((r) => [r.customer_Id, r])).values());
        setAutocompleteData(unique);
        setShowAutocomplete(true);
      } catch (error) {
        if (!isMounted) return;
        setAutocompleteData([]);
        setShowAutocomplete(false);
      }
    };

    fetchSuggestions();
    return () => {
      isMounted = false;
    };
  }, [debouncedCustomerSearch, dispatch]);

  const selectCustomerData = (customerData) => {
    setSelectedCustomer(customerData);
    setCustomerSearch('');

    setCustomerParam({
      customer_Id: customerData.customer_Id,
      customer_Name: customerData.customer_Name,
    });

    setFormData((prev) => ({
      ...prev,
      customer_Id: customerData.customer_Id,
      customer_Name: customerData.customer_Name,
    }));

    setAutocompleteData([]);
    setShowAutocomplete(false);
  };

  const handleSuggestionSelect = (customerData, event) => {
    event.preventDefault();
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    selectCustomerData(customerData);
  };

  const findDoc = async () => {
    const customerId =
      Number(customerParam.customer_Id) ||
      Number(formData.customer_Id) ||
      Number(selectedCustomer?.customer_Id) ||
      0;

    if (customerId > 0) {
      setIsLoading(true);

      let param = {
        customer_Id: customerId,
      };
      try {
        const { payload } = await dispatch(getJobDocList({ inputData: param }));
        // console.log(payload?.data);

        if (!checkEmpty(payload?.data)) {
          setProcessInvoice(true);
          setDocList(payload?.data);

          setFormData((prev) => ({
            ...prev,
            job_list: payload?.data,
          }));
        } else {
          toastMessage('error', 'No DOC found for this customer.');
        }
      } catch (error) {
        toastMessage('error', 'Server Side Error!');
      } finally {
        setIsLoading(false); // Hide loader after API response
      }
    } else {
      toastMessage('error', 'Enter valid customer');
    }
  };

  const getChargeAmount = (item, charge_Code) => {
    var charge = 0;
    if (charge_Code === 'other_charges') {
      for (let i in item.jobCharge) {
        if (
          item.jobCharge[i].charge_Code != 'clearing_charges' &&
          item.jobCharge[i].charge_Code != 'custom_duty' &&
          item.jobCharge[i].charge_Code != 'handling_charge' &&
          item.jobCharge[i].charge_Code != 'delivery_order_charge'
        ) {
          charge += parseFloat(item.jobCharge[i].charge_value);
        }
      }
      charge = formatTotal(charge);
    } else {
      charge = Object.values(item.jobCharge).find((value) => value.charge_Code === charge_Code);
      charge = charge ? formatTotal(charge.charge_value) : 0; // Default value if not found
    }
    return charge;
  };

  const removeFromDocList = (item, index) => {
    setDocList((prevList) => prevList.filter((_, i) => i !== index));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'invoice_Date') {
      setFormData({
        ...formData,
        [name]: format(new Date(value), 'yyyy-MM-dd'), // Format the date
      });
    } else {
      // Check for special characters in customer_Name
      // if (name === "invoice_No") {
      //     const isValid = /^[a-zA-Z\s]+$/.test(value); // Allows only letters and spaces
      //     if (!isValid) {
      //         toastMessage("error", "Special characters are not allowed in the input.");
      //         return;
      //     }
      // }

      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const submitInvoice = async () => {
    console.log(formData);
    setIsSaveDisabled(true);
    if (!checkEmpty(formData.job_list)) {
      const updatedPaylod = {
        ...formData,
        job_list: docList,
      };

      const { payload } = await dispatch(addInvoice({ inputData: updatedPaylod }));
      // console.log(payload);
      if (!checkEmpty(payload?.msg)) {
        setIsSaveDisabled(false);
        toastMessage(payload?.status, payload?.msg);
      }
      if (payload?.status === 'success') {
        console.log('Invoice created successfully', payload);

        setIsSaveDisabled(false);
        // setTimeout(() => {
        goToReport();
        // }, 1000);
      }
    }
  };

  const goToReport = () => {
    navigate('/invoice/report');
  };

  return (
    <>
      <ToastContainer />
      <PageWrapper
        pageName="Create Invoice"
        headerBtnTitle="Report >"
        onHeaderButtonClick={() => {
          goToReport();
        }}
        showAddBtn={true}
      >
        <Row className="row-xs mb-2">
          <CCol md={4}>
            <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="customer_Name">
              Customer Name
            </CFormLabel>
            <div className="hsc-wrapper">
              <CFormInput
                type="text"
                id="customer_Name"
                name="customer_Name"
                value={customerParam.customer_Name}
                onChange={getCustomerList}
                required
                autoComplete="false"
                // onMouseDown={(e) => e.preventDefault()}
                onFocus={() => {
                  if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
                  if (autocompleteData.length > 0) setShowAutocomplete(true);
                }}
                onBlur={() => {
                  // delay so suggestion click can happen
                  blurTimerRef.current = setTimeout(() => {
                    setShowAutocomplete(false);

                    // if a customer was selected earlier, keep it.
                    // if not selected, do nothing (user typed something random).
                  }, 150);
                }}
              />
              {autocompleteData?.length > 0 && showAutocomplete && (
                <div
                  className="header-search-container"
                  onMouseDown={(e) => e.preventDefault()}
                  onPointerDown={(e) => e.preventDefault()}
                >
                  <div className="hs">
                    {/* <Container fluid> */}
                    {/* <Row className="d-flex justify-content-between mt-3 align-items-stretch"> */}
                    {autocompleteData?.map((suggestion, index) => (
                      // <div>
                      <div
                        key={suggestion.customer_Id || index}
                        className="main-message-list header-search-element"
                        onPointerDown={(e) => handleSuggestionSelect(suggestion, e)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="user-name">
                          {' '}
                          {suggestion?.customer_Name} - {suggestion?.customer_Code}
                        </span>
                        <span>{suggestion?.customer_Email}</span>
                        <span>{suggestion?.customer_Mobile}</span>
                      </div>
                      // </div>
                    ))}
                    {/* </Row> */}
                    {/* </Container> */}
                  </div>
                </div>
              )}
            </div>
            <CFormFeedback invalid>Enter customer name</CFormFeedback>
          </CCol>
          <CCol md={4}>
            {/* <CButton color="primary" className="btn btn-sm btn-loaders ms-2" type="button" style={{marginTop: "3em"}} onClick={() => {findDoc()}}>Find DOC</CButton> */}
            <Row className="btn-animation" style={{ marginTop: '2.5em' }}>
              <Button
                variant=""
                type="button"
                className={`btn btn-sm btn-primary ${isLoading ? 'btn-loaders' : ''} ms-2`}
                onClick={() => {
                  findDoc();
                }}
                disabled={isLoading}
              >
                {isLoading ? <span className="loading">Find DOC</span> : 'Find DOC'}
              </Button>
            </Row>
          </CCol>
        </Row>
      </PageWrapper>

      {!checkEmpty(customerParam.customer_Id) && processInvoice && !checkEmpty(docList) && (
        <PageWrapper pageName="Process Invoice">
          <Row className="row-xs mb-4">
            {/* <CCol md={2}>
                            <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="invoice_No">Invoice No</CFormLabel>
                            <CFormInput
                                type="text"
                                id="invoice_No"
                                name="invoice_No"
                                value={formData.invoice_No}
                                onChange={handleInputChange}
                                required
                                // pattern="^[a-zA-Z\s]+$" // Ensures no special characters
                            />
                            <CFormFeedback invalid>Enter invoice no</CFormFeedback>
                        </CCol> */}
            <CCol md={3}>
              <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="invoice_Date">
                Invoice Date
              </CFormLabel>
              <InputGroup className="input-group reactdate-pic">
                <InputGroup.Text className="input-group-text">
                  <i className="typcn typcn-calendar-outline tx-24 lh--9 op-6"></i>
                </InputGroup.Text>
                <DatePicker
                  className="form-control"
                  name="invoice_Date"
                  id="invoice_Date"
                  selected={formData.invoice_Date ? new Date(formData.invoice_Date) : null} // Convert to Date object
                  onChange={(date) => {
                    const event = {
                      target: {
                        name: 'invoice_Date',
                        value: date,
                      },
                    };
                    handleInputChange(event); // Simulate an event for compatibility
                  }}
                  dateFormat="yyyy-MM-dd" // Match the desired format
                  // isClearable // Optional: Allow clearing the date
                />
              </InputGroup>
              <CFormFeedback invalid>Enter Invoice Date</CFormFeedback>
            </CCol>
            <CCol md={9}>
              <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="invoice_Remarks">
                Remarks
              </CFormLabel>
              <CFormInput
                type="text"
                id="invoice_Remarks"
                name="invoice_Remarks"
                value={formData.invoice_Remarks}
                onChange={handleInputChange}
                required
              />
              <CFormFeedback invalid>Enter invoice remarks</CFormFeedback>
            </CCol>
            {/* <CCol md={9}>
                <CFormLabel
                  className="main-content-label tx-11 tx-medium tx-gray-600"
                  htmlFor="custom_declaration"
                >
                  Custom Declarations
                </CFormLabel>
                <CFormInput
                  type="text"
                  id="custom_declaration"
                  name="custom_declaration"
                  value={formData.custom_declaration}
                  onChange={handleInputChange}
                  required
                />
                <CFormFeedback invalid>Enter Custom Declarations</CFormFeedback>
              </CCol> */}
          </Row>
          <Row className="row-xs mb-4">
            <CCol md={3}>
              <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="invoice_AWBL_Number">
                BL NO/AWBL NO
              </CFormLabel>
              <CFormInput
                type="text"
                id="invoice_AWBL_Number"
                name="invoice_AWBL_Number"
                value={formData.invoice_AWBL_Number}
                onChange={handleInputChange}
                required
              />
            </CCol>
            <CCol md={9}>
              <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="invoice_Mode">
                Mode
              </CFormLabel>
              <Form.Select
                className="form-control"
                id="invoice_Mode"
                name="invoice_Mode"
                value={formData.invoice_Mode}
                onChange={handleInputChange}
              >
                <option value="">Select</option>
                <option value="Land">Land</option>
                <option value="Air">Air</option>
                <option value="Sea">Sea</option>
              </Form.Select>
            </CCol>
          </Row>
          <Table className="table table-bordered text-nowrap mb-0">
            <thead>
              <tr>
                <th className="text-center">#</th>
                <th className="text-center">Doc No</th>
                <th className="text-center">No Of Veh</th>
                <th className="text-center">Clearing Charges</th>
                <th className="text-center">Customs Duty</th>
                <th className="text-center">Handling Charges</th>
                <th className="text-center">Delivery Order</th>
                <th className="text-center">Other Charges</th>
                <th className="text-center">Total Amount</th>
                <th className="text-center"></th>
              </tr>
            </thead>
            <tbody>
              {docList?.map((item, index) => (
                <tr key={index}>
                  <td className="text-center">{index + 1}</td>
                  <td className="text-center">{item.doc_No}</td>
                  <td className="text-center">{item.job_Load_No}</td>
                  <td className="text-center">{`QAR ${getChargeAmount(item, 'clearing_charges')}`}</td>
                  <td className="text-center">{`QAR ${getChargeAmount(item, 'custom_duty')}`}</td>
                  <td className="text-center">{`QAR ${getChargeAmount(item, 'handling_charge')}`}</td>
                  <td className="text-center">{`QAR ${getChargeAmount(item, 'delivery_order_charge')}`}</td>
                  <td className="text-center">{`QAR ${getChargeAmount(item, 'other_charges')}`}</td>
                  <td className="text-center">
                    QAR{' '}
                    {formatTotal(
                      checkFloat(getChargeAmount(item, 'clearing_charges')) +
                        checkFloat(getChargeAmount(item, 'custom_duty')) +
                        checkFloat(getChargeAmount(item, 'handling_charge')) +
                        checkFloat(getChargeAmount(item, 'delivery_order_charge')) +
                        checkFloat(getChargeAmount(item, 'other_charges')),
                    )}
                  </td>
                  <td className="text-center">
                    <CButton
                      color="danger"
                      className="btn btn-sm mt-1"
                      onClick={() => {
                        removeFromDocList(item, index);
                      }}
                    >
                      -
                    </CButton>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th className="text-center" colSpan="2">
                  Total
                </th>
                <th className="text-center">{docList.reduce((sum, item) => sum + Number(item.job_Load_No), 0)}</th>
                <th className="text-center">
                  QAR{' '}
                  {formatTotal(
                    docList.reduce((sum, item) => sum + checkFloat(getChargeAmount(item, 'clearing_charges')), 0),
                  )}
                </th>
                <th className="text-center">
                  QAR{' '}
                  {formatTotal(
                    docList.reduce((sum, item) => sum + checkFloat(getChargeAmount(item, 'custom_duty')), 0),
                  )}
                </th>
                <th className="text-center">
                  QAR{' '}
                  {formatTotal(
                    docList.reduce((sum, item) => sum + checkFloat(getChargeAmount(item, 'handling_charge')), 0),
                  )}
                </th>
                <th className="text-center">
                  QAR{' '}
                  {formatTotal(
                    docList.reduce((sum, item) => sum + checkFloat(getChargeAmount(item, 'delivery_order_charge')), 0),
                  )}
                </th>
                <th className="text-center">
                  QAR{' '}
                  {formatTotal(
                    docList.reduce((sum, item) => sum + checkFloat(getChargeAmount(item, 'other_charges')), 0),
                  )}
                </th>
                <th className="text-center">
                  QAR{' '}
                  {formatTotal(
                    docList.reduce(
                      (sum, item) =>
                        sum +
                        (checkFloat(getChargeAmount(item, 'clearing_charges')) +
                          checkFloat(getChargeAmount(item, 'custom_duty')) +
                          checkFloat(getChargeAmount(item, 'handling_charge')) +
                          checkFloat(getChargeAmount(item, 'delivery_order_charge')) +
                          checkFloat(getChargeAmount(item, 'other_charges'))),
                      0,
                    ),
                  )}
                </th>
                <th className="text-center"></th>
              </tr>
            </tfoot>
          </Table>
          <Row className="row-xs mt-4">
            <CCol md={12}>
              <Row className="btn-animation">
                <Button
                  variant=""
                  type="button"
                  className={`btn btn-sm btn-primary ${isSaveDisabled ? 'btn-loaders' : ''} ms-2`}
                  onClick={() => {
                    submitInvoice();
                  }}
                  disabled={isSaveDisabled}
                >
                  {isSaveDisabled ? <span className="loading">SAVE</span> : 'SAVE'}
                </Button>
              </Row>
            </CCol>
          </Row>
        </PageWrapper>
      )}
    </>
  );
};

export default InvoiceCreate;
